import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr, uploadFile, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, todayISO, formatDate } from "@/lib/format";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export const ITEM_PRESET = ["Oli Mesin", "Oli Transmisi", "Oli Gardan", "Filter Udara", "Filter Oli", "Filter Solar / Bensin", "Filter Lain-lain", "Ban Kendaraan", "Aki", "Kampas Rem", "Busi", "Jasa Service"];
const emptyItem = () => ({ nama: "", qty: 1, harga_satuan: 0 });

export const VehicleSelect = ({ vehicles, value, onChange, testid, all }) => (
  <Select value={value || (all ? "all" : "")} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Pilih kendaraan" /></SelectTrigger>
    <SelectContent>
      {all && <SelectItem value="all">Semua Kendaraan</SelectItem>}
      {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plat_no} · {v.jenis}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const FotoField = ({ value, onChange, testid }) => {
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const r = await uploadFile(file, "bukti"); onChange(r.path); toast.success("Foto nota terunggah"); } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={fileUrl(value)} alt="nota" className="h-10 w-10 rounded object-cover border" />}
      <label className="cursor-pointer text-sm text-blue-700 underline"><input type="file" accept="image/*" capture="environment" className="hidden" data-testid={testid} onChange={up} />{value ? "Ganti foto" : "Unggah / foto nota"}</label>
    </div>
  );
};

export const canEditRow = (user, r) => user?.role !== "site_admin" || r.mandor_id === user?.employee_id;

export default function VehicleServices({ vehicles }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [vid, setVid] = useState("all");
  const [form, setForm] = useState(null);
  const [foto, setFoto] = useState(null);
  const load = () => api.get(`/vehicle-services${vid !== "all" ? `?vehicle_id=${vid}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vid]);
  const total = useMemo(() => form ? form.items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.harga_satuan || 0), 0) : 0, [form]);
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const save = async () => {
    if (!form.vehicle_id) return toast.error("Pilih kendaraan");
    if (!form.items.some((it) => it.nama.trim())) return toast.error("Isi minimal satu item");
    try {
      if (form.id) await api.put(`/vehicle-services/${form.id}`, form); else await api.post("/vehicle-services", form);
      toast.success("Data service tersimpan"); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Hapus service ${r.plat_no} tanggal ${formatDate(r.tanggal)}?`)) return;
    try { await api.delete(`/vehicle-services/${r.id}`); toast.success("Data service dihapus"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const v = vehicles.find((x) => x.id === form?.vehicle_id);
  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b">
        <div className="w-full sm:w-64"><VehicleSelect vehicles={vehicles} value={vid} onChange={setVid} testid="svc-filter-vehicle" all /></div>
        <div className="text-sm text-slate-500 flex-1">Total: <b data-testid="svc-grand-total">{rupiah(rows.reduce((s, r) => s + Number(r.total || 0), 0))}</b> ({rows.length} service)</div>
        <Button className="bg-blue-800 hover:bg-blue-900" data-testid="svc-add-btn"
          onClick={() => setForm({ tanggal: todayISO(), vehicle_id: vid !== "all" ? vid : "", km: "", bengkel: "", items: [emptyItem()], keterangan: "", foto_path: "" })}>
          <Plus className="h-4 w-4 mr-1" /> Catat Service</Button>
      </div>
      <div className="overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead><tr className="bg-blue-900 text-white text-xs uppercase">
            {["Tanggal", "Plat No", "Jenis", "KM", "Bengkel", "Item", "Total", "Nota", "Diinput", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-slate-400">Belum ada catatan service.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`svc-row-${i}`}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{formatDate(r.tanggal)}</td><td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{r.plat_no}</td>
                <td className="px-3 py-2">{r.jenis}</td><td className="px-3 py-2 font-mono">{Number(r.km || 0).toLocaleString("id-ID")}</td><td className="px-3 py-2">{r.bengkel || "-"}</td>
                <td className="px-3 py-2 text-xs">{(r.items || []).map((it, j) => <div key={j}>{it.nama} × {it.qty} — {rupiah(it.jumlah)}</div>)}</td>
                <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">{rupiah(r.total)}</td>
                <td className="px-3 py-2">{r.foto_path ? <button className="text-blue-700" onClick={() => setFoto(fileUrl(r.foto_path))} data-testid={`svc-foto-${i}`}><ImageIcon className="h-4 w-4" /></button> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-xs">{r.mandor_nama || r.created_by}</td>
                <td className="px-3 py-2 whitespace-nowrap">{canEditRow(user, r) && <>
                  <Button size="icon" variant="ghost" data-testid={`svc-edit-${i}`} onClick={() => setForm({ ...r, items: r.items.map(({ nama, qty, harga_satuan }) => ({ nama, qty, harga_satuan })) })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" data-testid={`svc-del-${i}`} onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="svc-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Ubah" : "Catat"} Perawatan & Service</DialogTitle></DialogHeader>
          {form && <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <F label="Tanggal"><Input type="date" value={form.tanggal} data-testid="svc-f-tanggal" onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></F>
              <div className="sm:col-span-2"><F label="Kendaraan"><VehicleSelect vehicles={vehicles.filter((x) => x.aktif !== false || x.id === form.vehicle_id)} value={form.vehicle_id} onChange={(x) => setForm({ ...form, vehicle_id: x })} testid="svc-f-vehicle" /></F></div>
              <F label="KM saat service"><Input type="number" value={form.km} data-testid="svc-f-km" onChange={(e) => setForm({ ...form, km: e.target.value })} /></F>
            </div>
            {v && <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="svc-vehicle-info"><b>{v.plat_no}</b> · {v.jenis}{v.merk ? ` · ${v.merk}` : ""}{v.tahun ? ` · ${v.tahun}` : ""}</div>}
            <F label="Bengkel"><Input value={form.bengkel} data-testid="svc-f-bengkel" onChange={(e) => setForm({ ...form, bengkel: e.target.value })} /></F>
            <div className="rounded-lg border border-slate-200">
              <div className="grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-xs font-bold uppercase text-slate-500">
                <div className="col-span-5">Item</div><div className="col-span-2 text-right">Qty</div><div className="col-span-2 text-right">Harga</div><div className="col-span-3 text-right">Jumlah</div>
              </div>
              <datalist id="svc-items">{ITEM_PRESET.map((p) => <option key={p} value={p} />)}</datalist>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t items-center" data-testid={`svc-item-${i}`}>
                  <Input list="svc-items" className="col-span-5 h-9" placeholder="Oli Mesin / ketik item lain" value={it.nama} data-testid={`svc-item-nama-${i}`} onChange={(e) => setItem(i, "nama", e.target.value)} />
                  <Input type="number" className="col-span-2 h-9 text-right" value={it.qty} data-testid={`svc-item-qty-${i}`} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input type="number" className="col-span-2 h-9 text-right" value={it.harga_satuan} data-testid={`svc-item-harga-${i}`} onChange={(e) => setItem(i, "harga_satuan", e.target.value)} />
                  <div className="col-span-3 flex items-center justify-end gap-1 text-sm font-medium">{rupiah(Number(it.qty || 0) * Number(it.harga_satuan || 0))}
                    {form.items.length > 1 && <button className="text-rose-500" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}><X className="h-4 w-4" /></button>}
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t">
                <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))} data-testid="svc-add-item"><Plus className="h-4 w-4 mr-1" /> Tambah Item</Button>
                {ITEM_PRESET.map((p) => (
                  <button key={p} className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-orange-50" data-testid={`svc-preset-${p}`}
                    onClick={() => setForm((f) => ({ ...f, items: [...f.items.filter((x) => x.nama.trim()), { ...emptyItem(), nama: p }] }))}>+ {p}</button>
                ))}
                <div className="ml-auto text-sm">Total: <b data-testid="svc-total">{rupiah(total)}</b></div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Keterangan"><Input value={form.keterangan} data-testid="svc-f-ket" onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></F>
              <F label="Foto Nota"><FotoField value={form.foto_path} onChange={(p) => setForm((f) => ({ ...f, foto_path: p }))} testid="svc-f-foto" /></F>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="svc-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!foto} onOpenChange={(o) => { if (!o) setFoto(null); }}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Foto Nota</DialogTitle></DialogHeader>{foto && <img src={foto} alt="nota" className="w-full rounded" />}</DialogContent>
      </Dialog>
    </Card>
  );
}
